import React, { useCallback } from "react";
import { default as Tooltip, OverflowTooltip } from "@adsk/alloy-react-tooltip";
import { displayDate } from "@adsk/alloy-react-datetime";
import { getDateStringAsDisplayText } from "@adsk/acc-issues-common/utils/date/dateUtils";
import {
  CellRenderer,
  CellRendererArgs,
} from "../../../utils/types/tableTypes";
import { StatusDisplay } from "@adsk/acc-issues-common/components/display/StatusDisplay";
import { TypeDisplay } from "@adsk/acc-issues-common/components/display/TypeDisplay";
import { LocationDisplay } from "../../../input-field-components/LocationDisplay";
import { InlineValueDisplay } from "../../../utils/components/InlineValueDisplay";
import { DueDateDisplay } from "../../../input-field-components/DueDateDisplay";
import {
  Issue,
  IssueStatus,
  IssueCustomAttributeType,
  IssueListCustomAttributeOption,
} from "@adsk/acc-issues-types";
import {
  InlineLoader,
  LoaderStyle,
} from "@adsk/acc-issues-common/components/primitive/InlineLoader";
import { PlacementDisplay } from "../../../IssueDetailPanel/IssueDetailTabPanel/PlacementDisplay";
import { AttachmentsDisplay } from "../../../input-field-components/AttachmentsDisplay";
import { PlacementRevisionBadge } from "../../../input-field-components/PlacementRevisionBadge";
import { getPlacementOriginContext } from "@adsk/acc-issues-core/utils/data/placement";
import styled from "styled-components";
import Checkbox, { Indeterminate } from "@adsk/alloy-react-checkbox";
import { Table, Row } from "@tanstack/react-table";
import { ActionButton, MenuItemType } from "@adsk/alloy-react-menu";
import { ExportIcon } from "@adsk/alloy-react-icon";
import { useTranslations } from "@adsk/acc-issues-translations";
import { CouldNotDisplayPlacement } from "@adsk/acc-issues-common/components/display/CouldNotDisplayPlacement";
import Button from "@adsk/alloy-react-button";
import Theme from "@adsk/alloy-react-theme";
import { CellContentWrapper, CheckboxContainer } from "./issuesTableStyles";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getIssueTableSelectedIds } from "@adsk/acc-issues-core/state/redux/ducks/issueTableSelection";
import { trackEditSelectAll } from "../../../utils/analytics/issueActionsTracking";

/**
 * Wraps a function to create a CellRenderer function, that by default shows "-" is the data is nullish.
 */
export function createSimpleCellRenderer<TCell = string, TRow = any>(
  render: (cellRendererArgs: CellRendererArgs<TCell, TRow>) => any
): CellRenderer {
  return (args: CellRendererArgs<TCell, TRow>): React.ReactNode => {
    const empty = !args.cellData;
    const value = !empty ? render(args) : null;

    return <InlineValueDisplay empty={empty}>{value}</InlineValueDisplay>;
  };
}

/**
 *
 * @param cellData - a date string of the format `YYYY-MM-DD`
 *
 */
export const dateCellRenderer = createSimpleCellRenderer(({ cellData }) =>
  getDateStringAsDisplayText(cellData)
);

/**
 *
 * @param cellData - an ISO datetime string, or anything else that `Date` accepts in its constructor
 */
export const datetimeCellRenderer = createSimpleCellRenderer(({ cellData }) =>
  displayDate(new Date(cellData), {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
);

export const dueDateCellRenderer = createSimpleCellRenderer<
  { dueDate?: Issue["dueDate"]; closedBy?: Issue["closedBy"] },
  Issue
>(({ rowData }) => (
  <DueDateDisplay dueDate={rowData.dueDate} closedBy={rowData.closedBy} />
));

export const statusCellRenderer = createSimpleCellRenderer<IssueStatus>(
  ({ cellData }) => <StatusDisplay status={cellData} />
);

export const attachmentsCellRenderer = ({ rowData }: { rowData: Issue }) => {
  return <AttachmentsDisplay issue={rowData} />;
};

export const typeCellRenderer = createSimpleCellRenderer<
  { code: string; title: string },
  Issue
>(({ cellData, rowData }) => {
  if (!cellData.title || !cellData.code) {
    return InlineValueDisplay.EMPTY;
  }

  return (
    <TypeDisplay
      status={rowData.status}
      stampCode={cellData.code}
      title={cellData.title}
      stampSize={32}
      isMultiLineAllowed={false}
    />
  );
});

export const locationCellRenderer = ({ cellData }: { cellData: any }) => (
  <LocationDisplay locationId={cellData} />
);

const PlacementWrapper = styled.div`
  display: flex;
  gap: 5px;
  width: 100%;
  align-items: center;
`;

const PlacementTextWrapper = styled.div`
  white-space: nowrap;
  overflow: hidden;
`;

const PlacementBadgeWrapper = styled.div`
  flex-shrink: 0;
`;

const ActionCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  outline: none;

  && button:hover {
    background-color: transparent;
  }
`;

export const placementCellRenderer: CellRenderer = ({
  cellData,
  rowData,
  container,
}) => {
  if (cellData === undefined) {
    return <InlineLoader loaderStyle={LoaderStyle.Text} />;
  }

  const isDocumentPlacement = rowData.isDocumentPlacement;
  const isSheetPlacement = rowData.isSheetPlacement;
  const hasPlacementDisplayError = !!rowData?.hasPlacementDisplayError;
  const isPlacementDeleted = !!rowData?.isPlacementDeleted;

  const placementLink = rowData.placementLinkFunction({
    projectId: rowData.projectId,
    productId: rowData.productId,
    sheet: isSheetPlacement && cellData ? cellData : undefined,
    issueId: rowData.id,
    document: isDocumentPlacement && cellData ? cellData : undefined,
    originContext: getPlacementOriginContext(rowData),
  });

  if (!cellData) {
    if (hasPlacementDisplayError) {
      return <PlacementCellCouldNotDisplay container={container} />;
    }
    if (isPlacementDeleted) {
      return <PlacementCellDeleted container={container} />;
    }
    return InlineValueDisplay.EMPTY;
  }

  const documentTitle = cellData.name;
  const sheetTitle =
    cellData.name + (cellData.title ? ` (${cellData.title})` : "");

  const placementText = isSheetPlacement
    ? sheetTitle
    : isDocumentPlacement
    ? documentTitle
    : undefined;

  return (
    <PlacementWrapper>
      <PlacementTextWrapper>
        <OverflowTooltip
          style={{ width: "100%" }}
          autoTipOnEllipsis
          variant={Tooltip.VARIANTS.DARK}
          container={container}
          content={placementText}
        >
          <PlacementDisplay
            placementLink={placementLink}
            placementText={placementText}
            shouldShowLink={!rowData.deleted}
            issue={rowData}
          />
        </OverflowTooltip>
      </PlacementTextWrapper>
      <PlacementBadgeWrapper>
        <PlacementRevisionBadge issue={rowData} />
      </PlacementBadgeWrapper>
    </PlacementWrapper>
  );
};

const PlacementCellCouldNotDisplay = ({
  container,
}: {
  container?: HTMLElement;
}) => {
  return (
    <CouldNotDisplayPlacement container={container} showWarningIcon={false} />
  );
};

const PlacementCellDeleted = ({ container }: { container?: HTMLElement }) => {
  const { T } = useTranslations();
  const text = T.translate("activityLog.fileWasDeleted") as string;

  return (
    <Tooltip
      container={container}
      variant={Tooltip.VARIANTS.DARK}
      content={text}
    >
      <span>{text}</span>
    </Tooltip>
  );
};

export const renderText = (value: unknown): React.ReactNode => {
  let displayValue: unknown;

  if (typeof value !== "object") {
    displayValue = value;
  } else if (Array.isArray(value)) {
    displayValue = value.join(", ");
  } else {
    displayValue = String(value);
  }

  return (
    <CellContentWrapper>
      <InlineValueDisplay empty={false}>{displayValue}</InlineValueDisplay>
    </CellContentWrapper>
  );
};
const HeaderTextWrapper = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const renderHeaderWithTooltip = (
  text: React.ReactNode,
  container?: any
) => {
  return (
    <Tooltip
      container={container}
      variant={Tooltip.VARIANTS.DARK}
      content={text}
    >
      <HeaderTextWrapper>{text}</HeaderTextWrapper>
    </Tooltip>
  );
};

export const customAttributeCellRenderer = ({
  customAttribute,
  customField,
  formatLocalizedNumber,
}: {
  customAttribute: any;
  customField: any;
  formatLocalizedNumber: (value: string) => string;
}): React.ReactNode => {
  if (!customAttribute) {
    return null;
  }

  if (!customAttribute.value) {
    return renderText("-");
  }

  if (customAttribute?.type === IssueCustomAttributeType.Numeric) {
    return renderText(formatLocalizedNumber(customAttribute.value as string));
  }

  // Convert ID to display value for dropdown type in custom fields
  if (customField?.dataType === IssueCustomAttributeType.List) {
    const options = customField?.metadata?.list?.options;
    if (options) {
      const option = options.find(
        (opt: IssueListCustomAttributeOption) =>
          opt.id === customAttribute.value
      );
      return renderText(option ? option.value : "-");
    }
    return renderText("-");
  }

  return renderText(customAttribute.value || "-");
};

export const CheckboxHeaderCell = ({ table }: { table: Table<Issue> }) => {
  const { getRowModel, getSelectedRowModel } = table;
  const totalRows = getRowModel().rows.length;
  const selectedRows = getSelectedRowModel().rows.length;
  const areAllSelected = totalRows > 0 && totalRows === selectedRows;

  const selectedIssueIds = useSelector(getIssueTableSelectedIds);

  const hasSelectionsOnOtherPages =
    selectedRows === 0 && selectedIssueIds.length > 0;

  const isIndeterminate =
    (selectedRows > 0 && !areAllSelected) || hasSelectionsOnOtherPages;

  const handleSelectAll = (state: boolean | Indeterminate) => {
    if (state === true) {
      trackEditSelectAll();
    }
    table.toggleAllPageRowsSelected(state === true);
  };

  const checkedState = areAllSelected
    ? true
    : isIndeterminate
    ? Checkbox.INDETERMINATE
    : false;

  return (
    <CheckboxContainer>
      <Checkbox
        data-testid="select-all"
        checked={checkedState}
        onChange={handleSelectAll}
      />
    </CheckboxContainer>
  );
};

enum ActionKey {
  EXPORT = "EXPORT",
}

const StyledActionButton = styled(ActionButton)`
  color: ${Theme.colors.charcoal700};
  outline: none;
  background-color: transparent;
`;

export const ActionCell = React.memo(
  ({ row, table }: { row: Row<Issue>; table: Table<Issue> }) => {
    const { T } = useTranslations();
    const issue = row.original;
    const meta = table.options.meta as any;
    const onExport = meta?.onExport;
    const container = meta?.container;

    const handleAction = useCallback(
      (action: (string | number)[]) => {
        if (!action || !action.length) return;
        if (action[0] === ActionKey.EXPORT && onExport) {
          onExport(issue);
        }
      },
      [issue, onExport]
    );

    return (
      <ActionCellContainer
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <StyledActionButton
          horizontal={false}
          variant={Button.VARIANTS.TERTIARY}
          container={container}
          options={[
            {
              key: ActionKey.EXPORT,
              label: T.translate("issueTable.exportAsPdf"),
              renderIcon: (p: any) => <ExportIcon {...p} />,
            } as MenuItemType<ActionKey>,
          ]}
          onChange={handleAction}
          data-testid={`action-menu-${row.id}`}
        />
      </ActionCellContainer>
    );
  }
);
